import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePanic } from '../contexts/PanicContext';
import { Briefcase, Stethoscope, Plane, GraduationCap, Users, AlertCircle, BookOpen, Heart, ChevronRight, Zap, PenLine, Activity, Sparkles, Battery, Pill } from 'lucide-react';
import { Button } from '../components/ui/button';
import FlareMode from '../components/FlareMode';

const scenarios = [
  { id: 'workplace', name: 'Workplace', icon: Briefcase, description: 'HR, colleagues', color: 'bg-blue-50 border-blue-200 hover:border-blue-400' },
  { id: 'medical', name: 'Medical', icon: Stethoscope, description: 'Healthcare', color: 'bg-rose-50 border-rose-200 hover:border-rose-400' },
  { id: 'travel', name: 'Travel', icon: Plane, description: 'Airports, hotels', color: 'bg-sky-50 border-sky-200 hover:border-sky-400' },
  { id: 'school', name: 'School', icon: GraduationCap, description: 'Academic', color: 'bg-amber-50 border-amber-200 hover:border-amber-400' },
  { id: 'social', name: 'Social', icon: Users, description: 'Friends, family', color: 'bg-violet-50 border-violet-200 hover:border-violet-400' }
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { triggerPanic, setSelectedScenario } = usePanic();
  const [showFlareMode, setShowFlareMode] = useState(false);

  const handleScenarioClick = (scenarioId) => {
    setSelectedScenario(scenarioId);
    navigate(`/scenario/${scenarioId}`);
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen pb-safe" data-testid="dashboard">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <div className="page-enter">
          <p className="text-muted-foreground">Welcome back,</p>
          <h1 className="font-heading text-3xl font-semibold text-foreground mt-1">{firstName}</h1>
        </div>

        {/* Quick Action Buttons */}
        <div className="mt-6 flex gap-2 page-enter stagger-1">
          <Button
            data-testid="panic-button"
            onClick={() => triggerPanic('workplace')}
            className="btn-panic flex-1 flex items-center justify-center gap-2"
          >
            <AlertCircle className="h-5 w-5" />
            <span>Panic Mode</span>
          </Button>
          <Button
            data-testid="flare-mode-button"
            onClick={() => setShowFlareMode(true)}
            variant="outline"
            className="flex-1 flex items-center justify-center gap-2 bg-purple-50 border-purple-200 hover:bg-purple-100 hover:border-purple-300 text-purple-700"
          >
            <Battery className="h-5 w-5" />
            <span>Flare Day</span>
          </Button>
        </div>

        {/* Flare Mode Overlay */}
        <FlareMode isOpen={showFlareMode} onClose={() => setShowFlareMode(false)} />

        {/* Tools Grid */}
        <div className="mt-6 grid grid-cols-5 gap-2 page-enter stagger-1">
          <button onClick={() => navigate('/conditions')} className="card-soft p-3 text-center hover:border-purple-300">
            <BookOpen className="h-5 w-5 text-purple-500 mx-auto mb-1" />
            <span className="text-xs font-medium">Conditions</span>
          </button>
          <button onClick={() => navigate('/skills')} className="card-soft p-3 text-center hover:border-rose-300">
            <Zap className="h-5 w-5 text-rose-500 mx-auto mb-1" />
            <span className="text-xs font-medium">Skills</span>
          </button>
          <button onClick={() => navigate('/journal')} className="card-soft p-3 text-center hover:border-blue-300">
            <PenLine className="h-5 w-5 text-blue-500 mx-auto mb-1" />
            <span className="text-xs font-medium">Journal</span>
          </button>
          <button onClick={() => navigate('/symptoms')} className="card-soft p-3 text-center hover:border-amber-300">
            <Activity className="h-5 w-5 text-amber-500 mx-auto mb-1" />
            <span className="text-xs font-medium">Symptoms</span>
          </button>
          <button onClick={() => navigate('/medications')} className="card-soft p-3 text-center hover:border-green-300" data-testid="medications-link">
            <Pill className="h-5 w-5 text-green-500 mx-auto mb-1" />
            <span className="text-xs font-medium">Meds</span>
          </button>
        </div>

        {/* Skills CTA */}
        <button
          onClick={() => navigate('/skills')}
          className="mt-4 w-full card-soft bg-gradient-to-r from-rose-50 to-amber-50 border-rose-200/50 hover:border-rose-300 flex items-center justify-between page-enter stagger-2"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-xl shadow-sm">
              <Heart className="h-5 w-5 text-rose-500" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-foreground text-sm">Build Your Skills</h3>
              <p className="text-xs text-muted-foreground">Emotional, social & sensory exercises</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-rose-400" />
        </button>

        {/* Weekly Insights CTA */}
        <button
          onClick={() => navigate('/insights')}
          data-testid="insights-link"
          className="mt-2 w-full card-soft bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200/50 hover:border-purple-300 flex items-center justify-between page-enter stagger-2"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-xl shadow-sm">
              <Sparkles className="h-5 w-5 text-purple-500" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-foreground text-sm">Weekly Insights</h3>
              <p className="text-xs text-muted-foreground">AI analysis of your patterns</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-purple-400" />
        </button>

        {/* Scenarios */}
        <div className="mt-6">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3 page-enter stagger-2">Get a Script</h2>
          
          <div className="grid grid-cols-3 gap-2">
            {scenarios.slice(0, 3).map((scenario, index) => (
              <button
                key={scenario.id}
                data-testid={`scenario-${scenario.id}`}
                onClick={() => handleScenarioClick(scenario.id)}
                className={`card-soft ${scenario.color} text-left p-3 page-enter stagger-${index + 2}`}
              >
                <scenario.icon className="h-6 w-6 text-primary mb-1" />
                <h3 className="font-semibold text-foreground text-sm">{scenario.name}</h3>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {scenarios.slice(3).map((scenario, index) => (
              <button
                key={scenario.id}
                data-testid={`scenario-${scenario.id}`}
                onClick={() => handleScenarioClick(scenario.id)}
                className={`card-soft ${scenario.color} text-left p-3 page-enter stagger-4`}
              >
                <scenario.icon className="h-6 w-6 text-primary mb-1" />
                <h3 className="font-semibold text-foreground text-sm">{scenario.name}</h3>
              </button>
            ))}
          </div>
        </div>

        {/* Resources Link */}
        <button
          onClick={() => navigate('/resources')}
          className="mt-4 w-full card-soft bg-teal-50 border-teal-200 hover:border-teal-300 flex items-center justify-between page-enter stagger-4"
        >
          <div className="flex items-center gap-3">
            <Heart className="h-5 w-5 text-teal-500" />
            <span className="font-medium text-foreground text-sm">Resources & Support</span>
          </div>
          <ChevronRight className="h-5 w-5 text-teal-400" />
        </button>
        
        <div className="h-4" />
      </div>
    </div>
  );
}
